import { PAGE_TEMPLATES_API } from 'modules/funnels/funnel/api/page-templates-api'
import MobileSettingsHeader from 'modules/funnels/funnel/components/mobile-settings-header'
import PageTemplatePagination, {
  PageTemplateLimitType,
} from 'modules/funnels/funnel/components/page-template-selector/page-template-pagination'
import TemplateCard from 'modules/funnels/funnel/components/page-template-selector/template-card'
import { FunnelStepSettingTabsEnum } from 'modules/funnels/funnel/enums/funnel-step-setting-tabs-enum'
import { useFunnelStepsList } from 'modules/funnels/funnel/hooks/use-funnel-steps-list'
import { usePageTemplates } from 'modules/funnels/funnel/hooks/use-page-templates'
import { FunnelPageTemplateId } from 'modules/funnels/funnel/types/funnel-page-template-interface'
import { FunnelStepInterface } from 'modules/funnels/funnel/types/funnel-step-interface'
import { getPageTypeFromFunnelStepType } from 'modules/funnels/funnel/utils/get-page-type-from-funnel-step-type'
import { FunnelId } from 'modules/funnels/types/funnel-interface'
import React, { useEffect, useRef, useState } from 'react'
import { useUpdateData } from 'shared/hooks/use-update-data'
import useUser from 'shared/hooks/use-user'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { useIsTouchDevice } from 'shared/hooks/use-is-touch-device'
import { useOnClickOutside } from 'shared/hooks/use-onclick-outside'
import { Loader } from 'shared/components/loader'
import { useSelectPageTemplate } from '../../hooks/use-select-page-template'

interface PageTemplateSelectorProps {
  funnelStep: FunnelStepInterface | null
  funnelId: FunnelId
}

function PageTemplateSelector({ funnelStep, funnelId }: PageTemplateSelectorProps) {
  const { user } = useUser()

  const [limit, setLimit] = useState<PageTemplateLimitType>(9)

  const { pagination, setPagination } = usePagination(true)
  const { selectPageTemplate, isFetching } = useSelectPageTemplate()
  const limiter = { limit, setLimit }
  const paginator = { pagination, setPagination }

  const { mutateFunnelStepsList } = useFunnelStepsList(funnelId)
  const { pageTemplates } = usePageTemplates({
    type: getPageTypeFromFunnelStepType(funnelStep?.type),
    limit,
    pagination,
  })

  const isTouchDevice = useIsTouchDevice()

  const updateStepTemplatesData = useUpdateData({
    api: PAGE_TEMPLATES_API,
  })

  const [selectedPageTemplate, setSelectedPageTemplate] = useState<
    FunnelPageTemplateId | undefined
  >()

  const templatesContainerRef = useRef<HTMLDivElement>(null)

  const onSelect = async (pageTemplateId: FunnelPageTemplateId) => {
    try {
      if (funnelStep) {
        setSelectedPageTemplate(pageTemplateId)
        const response = await selectPageTemplate(funnelStep.id, pageTemplateId)
        await mutateFunnelStepsList(data => {
          if (data) {
            return data.map(dataFunnelStep => {
              if (dataFunnelStep.id === funnelStep.id) {
                return response
              }
              return dataFunnelStep
            })
          }
        }, false)
        setSelectedPageTemplate(undefined)
      }
    } catch (error) {
      setSelectedPageTemplate(undefined)
    }
  }

  useEffect(() => {
    const revalidateStepTemplates = async () => await updateStepTemplatesData(false, pageTemplates)
    if (pageTemplates) {
      revalidateStepTemplates()
    }
  }, [user?.dashboardLocale])

  useOnClickOutside(templatesContainerRef, () => {
    if (selectedPageTemplate) {
      setSelectedPageTemplate(undefined)
    }
  })

  const handleSelectedPageTemplate = (id: FunnelPageTemplateId) => {
    if (selectedPageTemplate === id) {
      setSelectedPageTemplate(undefined)
    } else {
      setSelectedPageTemplate(id)
    }
  }

  return (
    <div data-test-table-is-loaded={!!pageTemplates}>
      {!pageTemplates || !funnelStep ? (
        <div className={'flex h-full items-center justify-center py-12'}>
          <Loader className={'scale-150'} />
        </div>
      ) : (
        <div className="mb-10 flex flex-col gap-5 px-[15px] lg:px-[15px]">
          <MobileSettingsHeader type={FunnelStepSettingTabsEnum.stepConfiguration} />
          <div
            className={`grid w-full grid-cols-1 justify-between gap-x-8 gap-y-14 xl:grid-cols-2 2xl:grid-cols-3`}
            ref={templatesContainerRef}
          >
            {pageTemplates.items?.map(template => {
              return (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onSelect={onSelect}
                  selectedPageTemplate={selectedPageTemplate}
                  isFetching={isFetching}
                  onClickCard={
                    isTouchDevice ? () => handleSelectedPageTemplate(template.id) : undefined
                  }
                />
              )
            })}
          </div>
          <PageTemplatePagination data={pageTemplates} limiter={limiter} paginator={paginator} />
        </div>
      )}
    </div>
  )
}

export default PageTemplateSelector
